import { useQueryClient } from "@tanstack/react-query"
import useApi from "../../hooks/useApi"
import useNotification from "../../hooks/useNotification"
import useTranslation from "../../hooks/useTranslation"
import generateEndpoint from "../../utils/generateEndpoint"
import pause from "../../utils/pause"
import runLogic from '../../../../common/logic/runLogic'

/**
 * Where a modal's answer goes.
 *
 * This is the thing task 33.1 said was implied by which component you imported.
 * A modal form that wrote back into the parent's store was `ModalForm`; one that
 * POSTed to the server was `ModalViewCard`. Same modal, same elements, same
 * footer — two components, because the destination was in the code instead of
 * the config. Now the entry says:
 *
 *   (nothing)                → { to: 'store' }   write back into the caller's store
 *   submit: { to: 'entity' } → POST or PUT the record, then refetch and close
 *
 * `store` is the default because every one of the fourteen `modalFormData`
 * entries wants it — a form modal is a fragment of the form that opened it, and
 * writes back into that form's state. Only `modalCardData` declares `entity`,
 * which is the difference between the two maps stated as config rather than as
 * two components. Tier 2 established that a form only declares what differs.
 *
 * **Two destinations, so this is a switch and not a registry.** The widget list
 * is a registry because it is open — anyone can add a widget. This set is closed
 * by what a modal can do at all: write to a store, call the server, or dispatch a
 * named action, and the third is `ModalDialog`, which §8 keeps separate on
 * purpose. A switch over a closed set of two is not the nine-deep ternary that
 * started this.
 *
 * The hook is called unconditionally and branches inside, so hook order never
 * depends on config.
 */
const useModalSubmit = ({
    submit = {},
    entity,
    path,
    queryArray,
    parentId,
    recordId,
    state,
    setState,
    statePath = [],
    onDone,
}) => {
    const { t } = useTranslation()
    const { postMe, putMe } = useApi()
    const queryClient = useQueryClient()
    const { showNotification, NotificationComponent } = useNotification()

    const toStore = async () => {
        // Writing at the root re-runs the record's logic, because the fields the
        // modal just changed may feed calculations. Writing at a path is a patch
        // into a sub-object and the parent card runs its own logic on save.
        if (statePath.length === 0) {
            const processedData = runLogic(state, state.entity)
            setState({ ...processedData, savedOn: new Date() })
        } else {
            setState({ ...state, savedOn: new Date() }, { path: statePath })
        }
        return { ok: true }
    }

    const toEntity = async () => {
        const endpoint = generateEndpoint(path || '', parentId)
        const response = recordId
            ? await putMe(endpoint, state, recordId)
            : await postMe(endpoint, state)

        if (response?.error) {
            showNotification(t(response.error.name), t(response.error.message), 'error')
            await queryClient.refetchQueries(queryArray)
            return { ok: false }
        }

        showNotification(t('success.updated'), '', 'success')
        await queryClient.refetchQueries(queryArray)
        // The pause is what the card modal has always done: let the refetched
        // list paint before the modal disappears out from under it.
        await pause(2000)
        // The saved record rides along, so a caller that opened the card for
        // a new record can use it — select it, link it (38.03 §7).
        return { ok: true, record: response }
    }

    /**
     * Returns `{ ok }`. A caller closes on `ok` and stays open otherwise, which
     * is the only reason the result exists — a rejected POST used to close the
     * modal and lose the edit.
     */
    const submitForm = async () => {
        const result = submit.to === 'entity' ? await toEntity() : await toStore()
        if (result.ok && typeof onDone === 'function') await onDone(result.record)
        return result
    }

    return { submitForm, NotificationComponent }
}

export default useModalSubmit
